import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { gruposAPI } from '../services/api';
import { toast } from 'react-toastify';
import '../Clientes.css';

const RESUMEN_FORMATO_OPTIONS = [
  { value: 'total_sin_detalle', label: 'Total resumido sin detallar integrantes' },
  { value: 'total_con_nombres_movimientos', label: 'Total con nombres y movimientos de cada uno (sin importes)' },
  { value: 'total_detallado_individual', label: 'Monto total del grupo detallando integrantes y movimientos en forma individual' },
  { value: 'total_saldos_integrantes', label: 'Saldo del grupo y saldo de cada integrante (sin movimientos)' },
];

const FACTURA_MODO_OPTIONS = [
  { value: '', label: 'No factura por grupo' },
  { value: 'cuenta_grupo', label: 'Una factura a una cuenta del grupo por el total' },
  { value: 'individual', label: 'Una factura por integrante con sus datos fiscales' },
];

const formatMoneda = (n) =>
  parseFloat(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const getSortValueIntegrante = (c, colId) => {
  switch (colId) {
    case 'cliente':
      return (c.razon_social || '').toLowerCase();
    case 'contacto':
      return `${c.celular || c.telefono || ''} ${c.email || ''}`.toLowerCase();
    case 'recibe':
      return c.recibe_comunicacion_grupo ? 1 : 0;
    case 'canal':
      return (c.canal_comunicacion || '').toLowerCase();
    case 'saldo':
      return parseFloat(c.saldo) || 0;
    default:
      return '';
  }
};

const CANAL_OPTIONS = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'ambos', label: 'WhatsApp y Email' },
];

const GrupoForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const destinatariosRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [clientesGrupo, setClientesGrupo] = useState([]);
  const [clientesDisponibles, setClientesDisponibles] = useState([]);
  const [clientesParaAgregar, setClientesParaAgregar] = useState([]);
  const [busquedaClienteDisponible, setBusquedaClienteDisponible] = useState('');
  const [busquedaIntegrantes, setBusquedaIntegrantes] = useState('');
  const [filtroDestinatarios, setFiltroDestinatarios] = useState('todos');
  const [filtroCanal, setFiltroCanal] = useState('todos');
  const [sortByColumn, setSortByColumn] = useState(null);
  const [sortAsc, setSortAsc] = useState(true);
  const [formData, setFormData] = useState({
    nombre: '',
    resumen_formato: 'total_sin_detalle',
    factura_modo: '',
    factura_cliente_id: '',
    activo: true,
  });

  const loadGrupo = useCallback(async () => {
    try {
      const response = await gruposAPI.getById(id);
      if (response.success) {
        setFormData({
          nombre: response.data.nombre,
          resumen_formato: response.data.resumen_formato || 'total_sin_detalle',
          factura_modo: response.data.factura_modo || '',
          factura_cliente_id: response.data.factura_cliente_id ? String(response.data.factura_cliente_id) : '',
          activo: response.data.activo !== false,
        });
      }
    } catch (error) {
      toast.error('Error al cargar grupo');
      navigate('/grupos');
    }
  }, [id, navigate]);

  const loadClientesGrupo = useCallback(async () => {
    if (!id) return;
    try {
      const res = await gruposAPI.getClientes(id);
      if (res.success && Array.isArray(res.data)) {
        setClientesGrupo(res.data);
      } else {
        setClientesGrupo([]);
      }
    } catch {
      setClientesGrupo([]);
    }
  }, [id]);

  const loadClientesDisponibles = useCallback(async () => {
    if (!id) return;
    try {
      const res = await gruposAPI.getClientesDisponibles(id);
      if (res.success && Array.isArray(res.data)) {
        setClientesDisponibles(res.data.filter((c) => !c.grupo_id || Number(c.grupo_id) !== Number(id)));
      }
    } catch {
      setClientesDisponibles([]);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadGrupo();
      loadClientesGrupo();
      loadClientesDisponibles();
    }
  }, [id, loadGrupo, loadClientesGrupo, loadClientesDisponibles]);

  useEffect(() => {
    if (window.location.hash === '#destinatarios' && destinatariosRef.current) {
      destinatariosRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [clientesGrupo, id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const clientesDisponiblesFiltrados = useMemo(() => {
    const q = busquedaClienteDisponible.trim().toLowerCase();
    if (!q) return clientesDisponibles;
    return clientesDisponibles.filter((c) =>
      (c.razon_social || '').toLowerCase().includes(q)
    );
  }, [clientesDisponibles, busquedaClienteDisponible]);

  const clientesGrupoVisibles = useMemo(() => {
    let list = [...clientesGrupo];
    const q = busquedaIntegrantes.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (c) =>
          (c.razon_social || '').toLowerCase().includes(q) ||
          (c.cuit || '').toLowerCase().includes(q) ||
          (c.telefono || '').toLowerCase().includes(q) ||
          (c.celular || '').toLowerCase().includes(q) ||
          (c.email || '').toLowerCase().includes(q)
      );
    }
    if (filtroDestinatarios === 'si') list = list.filter((c) => c.recibe_comunicacion_grupo);
    if (filtroDestinatarios === 'no') list = list.filter((c) => !c.recibe_comunicacion_grupo);
    if (filtroCanal !== 'todos') list = list.filter((c) => (c.canal_comunicacion || 'whatsapp') === filtroCanal);
    if (sortByColumn) {
      list.sort((a, b) => {
        const v1 = getSortValueIntegrante(a, sortByColumn);
        const v2 = getSortValueIntegrante(b, sortByColumn);
        const num = typeof v1 === 'number' && typeof v2 === 'number';
        let cmp = 0;
        if (num) cmp = v1 - v2;
        else cmp = String(v1).localeCompare(String(v2), 'es');
        return sortAsc ? cmp : -cmp;
      });
    }
    return list;
  }, [clientesGrupo, busquedaIntegrantes, filtroDestinatarios, filtroCanal, sortByColumn, sortAsc]);

  const handleSortColumn = (columnId) => {
    if (sortByColumn === columnId) {
      setSortAsc((prev) => !prev);
    } else {
      setSortByColumn(columnId);
      setSortAsc(true);
    }
  };

  const handleDestinatarioChange = (clienteId, field, value) => {
    setClientesGrupo((prev) =>
      prev.map((c) => (c.id === clienteId ? { ...c, [field]: value } : c))
    );
  };

  const agregarClientesAlGrupo = async () => {
    if (!id || clientesParaAgregar.length === 0) return;
    try {
      const ids = [...new Set([...clientesGrupo.map((c) => c.id), ...clientesParaAgregar.map(Number)])];
      await gruposAPI.assignClientes(id, ids);
      toast.success('Clientes agregados al grupo');
      setClientesParaAgregar([]);
      await loadClientesGrupo();
      await loadClientesDisponibles();
    } catch (e) {
      toast.error(e.message || 'Error al agregar clientes');
    }
  };

  const quitarClienteDelGrupo = async (clienteId) => {
    if (!id) return;
    const ids = clientesGrupo.filter((c) => c.id !== clienteId).map((c) => c.id);
    try {
      await gruposAPI.assignClientes(id, ids);
      if (String(clienteId) === formData.factura_cliente_id) {
        setFormData((prev) => ({ ...prev, factura_cliente_id: '' }));
      }
      toast.success('Cliente quitado del grupo');
      await loadClientesGrupo();
      await loadClientesDisponibles();
    } catch (e) {
      toast.error(e.message || 'Error al quitar cliente');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let response;
      if (id) {
        response = await gruposAPI.update(id, formData);
        if (response.success) {
          await gruposAPI.updateDestinatarios(
            id,
            clientesGrupo.map((c) => ({
              cliente_id: c.id,
              recibe_comunicacion_grupo: !!c.recibe_comunicacion_grupo,
              canal_comunicacion: c.canal_comunicacion || 'whatsapp',
            }))
          );
        }
      } else {
        response = await gruposAPI.create(formData);
      }
      if (response.success) {
        toast.success(id ? 'Grupo actualizado exitosamente' : 'Grupo creado exitosamente');
        if (id) {
          navigate('/grupos');
        } else {
          const newId = response.data?.id;
          navigate(newId ? `/grupos/editar/${newId}#destinatarios` : '/grupos');
        }
      }
    } catch (error) {
      toast.error(error.message || 'Error al guardar grupo');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cliente-form">
      <h1>{id ? 'Editar Grupo' : 'Nuevo Grupo'}</h1>

      <form onSubmit={handleSubmit} className="form-container">
        <div className="form-group">
          <label>Nombre *</label>
          <input
            type="text"
            name="nombre"
            value={formData.nombre}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Formato de envío de resúmenes *</label>
          <select
            name="resumen_formato"
            value={formData.resumen_formato}
            onChange={handleChange}
          >
            {RESUMEN_FORMATO_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <small style={{ color: '#7f8c8d', marginTop: '5px', display: 'block' }}>
            Define cómo se armará el resumen consolidado al enviar comunicaciones del grupo.
          </small>
        </div>

        <div className="form-group">
          <label>Facturación</label>
          <select name="factura_modo" value={formData.factura_modo} onChange={handleChange}>
            {FACTURA_MODO_OPTIONS
              .filter((opt) => id || opt.value !== 'cuenta_grupo')
              .map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
          </select>
          {!id && (
            <small style={{ color: '#7f8c8d', marginTop: '5px', display: 'block' }}>
              Para facturar a una cuenta del grupo, guardá el grupo y agregá los integrantes primero.
            </small>
          )}
        </div>

        {formData.factura_modo === 'cuenta_grupo' && (
          <div className="form-group">
            <label>Cuenta que recibe la factura *</label>
            <select name="factura_cliente_id" value={formData.factura_cliente_id} onChange={handleChange} required>
              <option value="">Elegí un integrante</option>
              {clientesGrupo.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.razon_social}</option>
              ))}
            </select>
            <small style={{ color: '#7f8c8d', marginTop: '5px', display: 'block' }}>
              La factura lleva el CUIT, la condición de IVA y el domicilio de esta cuenta, por el total de los integrantes.
            </small>
          </div>
        )}

        {id && (
          <div id="destinatarios" ref={destinatariosRef} className="form-group" style={{ scrollMarginTop: 80 }}>
            <h2 style={{ fontSize: '1.05rem', marginBottom: 8 }}>Envío de mensajes del grupo</h2>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 12, lineHeight: 1.5 }}>
              Marque qué integrantes reciben el resumen consolidado del grupo y por qué canal.
              Los demás integrantes no recibirán mensaje individual al usar envío <em>por grupo</em> en Clientes.
            </p>

            <div style={{ marginBottom: 16, padding: 12, background: '#f8f9fa', borderRadius: 6 }}>
              <label style={{ display: 'block', marginBottom: 6, fontWeight: 600 }}>Agregar clientes al grupo</label>
              <input
                type="search"
                placeholder="Buscar por nombre..."
                value={busquedaClienteDisponible}
                onChange={(e) => setBusquedaClienteDisponible(e.target.value)}
                style={{ width: '100%', marginBottom: 8, padding: '8px 10px', boxSizing: 'border-box' }}
              />
              <select
                multiple
                value={clientesParaAgregar.map(String)}
                onChange={(e) => {
                  const opts = Array.from(e.target.selectedOptions).map((o) => o.value);
                  setClientesParaAgregar(opts);
                }}
                style={{ width: '100%', minHeight: 100, marginBottom: 8 }}
              >
                {clientesDisponiblesFiltrados.length === 0 ? (
                  <option disabled value="">
                    {clientesDisponibles.length === 0
                      ? 'No hay clientes disponibles'
                      : 'Ningún cliente coincide con la búsqueda'}
                  </option>
                ) : (
                  clientesDisponiblesFiltrados.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.razon_social} {c.cuit ? `(CUIT ${c.cuit})` : ''}
                    </option>
                  ))
                )}
              </select>
              <button type="button" className="btn-secondary" onClick={agregarClientesAlGrupo} disabled={clientesParaAgregar.length === 0}>
                Agregar seleccionados
              </button>
            </div>

            {clientesGrupo.length > 0 ? (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12, alignItems: 'center' }}>
                  <input
                    type="search"
                    placeholder="Buscar integrante..."
                    value={busquedaIntegrantes}
                    onChange={(e) => setBusquedaIntegrantes(e.target.value)}
                    style={{ minWidth: 200, padding: '8px 10px', flex: '1 1 200px' }}
                  />
                  <select value={filtroDestinatarios} onChange={(e) => setFiltroDestinatarios(e.target.value)} style={{ padding: '8px 10px' }}>
                    <option value="todos">Todos los integrantes</option>
                    <option value="si">Solo destinatarios</option>
                    <option value="no">No destinatarios</option>
                  </select>
                  <select value={filtroCanal} onChange={(e) => setFiltroCanal(e.target.value)} style={{ padding: '8px 10px' }}>
                    <option value="todos">Todos los canales</option>
                    {CANAL_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      {[
                        { id: 'cliente', label: 'Cliente' },
                        { id: 'contacto', label: 'Contacto' },
                        { id: 'recibe', label: 'Recibe comunicaciones' },
                        { id: 'canal', label: 'Canal' },
                        { id: 'saldo', label: 'Saldo', align: 'right' },
                      ].map((col) => (
                        <th
                          key={col.id}
                          className="th-sortable"
                          style={col.align ? { textAlign: col.align } : undefined}
                          onClick={() => handleSortColumn(col.id)}
                        >
                          <span>{col.label}</span>
                          {sortByColumn === col.id && (
                            <span className="sort-indicator">{sortAsc ? ' ▲' : ' ▼'}</span>
                          )}
                        </th>
                      ))}
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientesGrupoVisibles.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="no-data">Ningún integrante coincide con los filtros</td>
                      </tr>
                    ) : (
                      clientesGrupoVisibles.map((c) => (
                        <tr key={c.id}>
                          <td>{c.razon_social}</td>
                          <td style={{ fontSize: 12 }}>
                            {c.celular || c.telefono || '-'}
                            {c.email ? ` · ${c.email}` : ''}
                          </td>
                          <td>
                            <input
                              type="checkbox"
                              checked={!!c.recibe_comunicacion_grupo}
                              onChange={(e) =>
                                handleDestinatarioChange(c.id, 'recibe_comunicacion_grupo', e.target.checked)
                              }
                            />
                          </td>
                          <td>
                            <select
                              value={c.canal_comunicacion || 'whatsapp'}
                              disabled={!c.recibe_comunicacion_grupo}
                              onChange={(e) =>
                                handleDestinatarioChange(c.id, 'canal_comunicacion', e.target.value)
                              }
                            >
                              {CANAL_OPTIONS.map((opt) => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                              ))}
                            </select>
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>
                            ${formatMoneda(c.saldo)}
                          </td>
                          <td>
                            <button type="button" className="btn-delete" onClick={() => quitarClienteDelGrupo(c.id)}>
                              Quitar
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              </>
            ) : (
              <p style={{ color: '#666', fontSize: 13 }}>
                No hay clientes en este grupo. Use el selector de arriba o asigne el grupo desde la{' '}
                <Link to="/clientes">ficha de cada cliente</Link>.
              </p>
            )}
          </div>
        )}

        <div className="form-group">
          <label className="form-toggle-wrap">
            <span className="toggle-switch-box">
              <input
                type="checkbox"
                name="activo"
                checked={formData.activo}
                onChange={handleChange}
              />
              <span className="form-toggle-switch" />
            </span>
            <span className="form-toggle-label">{formData.activo ? 'Desactivar' : 'Activar'}</span>
          </label>
        </div>

        <div className="form-actions">
          <button type="button" onClick={() => navigate('/grupos')} className="btn-secondary">
            Cancelar
          </button>
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GrupoForm;
